import { ApiError, HttpStatus, ValidationError } from '@connect-x-app/shared-domain-models';
import { AppConfig, EnvConfig } from '@connect-x-app/shared-types';
// eslint-disable-next-line import-x/no-named-as-default
import Axios, { AxiosError, AxiosResponse, ResponseType } from 'axios';
import qs from 'qs';

import { ErrorResponse, Headers, Params } from './http.types';
import { authorizationRequestInterceptor, errorResponseInterceptor, headerRequestInterceptor } from './httpInterceptors';

class HttpClient {
  private static config: EnvConfig;
  private static requestInterceptorResult: number;
  private static request401InterceptorResult: number;
  private static responseInterceptorResult: number;
  private static abortRequestInterceptorResult: number;
  private static abortController = new AbortController();

  static readonly axios = Axios.create({
    headers: HttpClient.getBasicHeaders(),
  });

  static setConfig(config: EnvConfig, platform: 'web' | 'ios' | 'android' | 'macos' | 'windows') {
    this.config = config;
    this.axios.defaults.baseURL = this.config.connectServiceApiHost;
    this.axios.defaults.headers['platform'] = platform;
  }

  static set401RequestInterceptor() {
    if (this.request401InterceptorResult) {
      this.axios.interceptors.request.eject(this.request401InterceptorResult);
    }

    this.request401InterceptorResult = this.axios.interceptors.request.use(authorizationRequestInterceptor);
  }

  static setAbortRequestInterceptor() {
    if (this.abortRequestInterceptorResult) {
      this.axios.interceptors.request.eject(this.abortRequestInterceptorResult);
    }

    this.abortRequestInterceptorResult = this.axios.interceptors.request.use(request => {
      request.signal = this.abortController.signal;
      return request;
    });
  }

  static abortInFlightRequests() {
    this.abortController.abort();
    // Re-arm immediately: aborting cancels what is in flight, it must not block future requests.
    // Re-arming later (on AppState 'active') races with the deeplink that resumes the app — a
    // request issued in that window would inherit the aborted signal and be rejected by axios
    // before it is ever dispatched. Re-arming here also keeps every in-flight request attached to
    // the current controller, so the next abort actually reaches all of them.
    this.abortController = new AbortController();
  }

  static setHeaders(appConfig: AppConfig) {
    if (this.requestInterceptorResult) {
      this.axios.interceptors.request.eject(this.requestInterceptorResult);
    }

    this.requestInterceptorResult = this.axios.interceptors.request.use(headerRequestInterceptor(appConfig));
  }

  static setInstances(onError: (response: ErrorResponse) => void) {
    this.responseInterceptorResult = this.axios.interceptors.response.use(
      response => response,
      errorResponseInterceptor(this.axios, onError),
    );
  }

  static resetInstances() {
    if (this.responseInterceptorResult) {
      this.axios.interceptors.response.eject(this.responseInterceptorResult);
    }

    if (this.requestInterceptorResult) {
      this.axios.interceptors.request.eject(this.requestInterceptorResult);
    }

    if (this.request401InterceptorResult) {
      this.axios.interceptors.request.eject(this.request401InterceptorResult);
    }
  }

  static getBasicHeaders(): Headers {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    } as Headers;
  }

  static createApiError(error: AxiosError<ApiError>): ApiError {
    if (error.response) {
      const data: {
        code?: string;
        error?: string;
        message?: string | Array<ValidationError & { property: string }>;
      } = error.response.data;
      return {
        error: data.error,
        message: this.mapErrorToMessage(data.message) || (data.code ?? undefined),
        statusCode: error.response.status,
        validationErrors: Array.isArray(data.message)
          ? data.message.reduce(
              (acc: Record<string, ValidationError>, { property, ...validationError }) => ({
                ...acc,
                [property]: validationError,
              }),
              {},
            )
          : {},
      };
    }
    return {
      message: error.message,
      statusCode: HttpStatus.INTERNAL_SERVER_ERROR,
    };
  }

  static async getRaw<T>(
    route: string,
    params: Params = {},
    headers: Headers = {} as Headers,
    responseType: ResponseType = 'json',
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.axios.get<T>(route, {
        headers,
        params,
        paramsSerializer: {
          serialize: params => {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        },
        responseType,
        withCredentials: true,
      });
    } catch (error) {
      const apiError = this.createApiError(error as AxiosError<ApiError>);
      throw Object.assign(new Error(apiError.message), apiError);
    }
  }

  static async get<T>(
    route: string,
    params: Params = {},
    headers: Headers = {} as Headers,
    responseType: ResponseType = 'json',
  ): Promise<T> {
    const result = await this.getRaw<T>(route, params, headers, responseType);
    return result.data;
  }

  static async put<T>(
    route: string,
    body: Record<string, unknown> = {},
    headers: Headers = {} as Headers,
    params: Params = {},
  ): Promise<T> {
    try {
      const result = await this.axios.put<T>(route, body, {
        headers,
        params,
        paramsSerializer: {
          serialize: params => {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        },
        withCredentials: true,
      });
      return result.data;
    } catch (error) {
      const apiError = this.createApiError(error as AxiosError<ApiError>);
      throw Object.assign(new Error(apiError.message), apiError);
    }
  }

  static async patch<T>(
    route: string,
    body: Record<string, unknown> = {},
    headers: Headers = {} as Headers,
    params: Params = {},
  ): Promise<T> {
    try {
      const result = await this.axios.patch<T>(route, body, {
        headers,
        params,
        paramsSerializer: {
          serialize: params => {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        },
        withCredentials: true,
      });
      return result.data;
    } catch (error) {
      const apiError = this.createApiError(error as AxiosError<ApiError>);
      throw Object.assign(new Error(apiError.message), apiError);
    }
  }

  static async post<T>(
    route: string,
    body: Record<string, unknown> | string | FormData = {},
    headers: Headers = {} as Headers,
    withCredentials = true,
  ): Promise<T> {
    try {
      const result = await this.axios.post<T>(route, body, {
        headers,
        // Prevent axios from trying to transform FormData
        transformRequest: [
          data => {
            if (data instanceof FormData) {
              return data;
            }
            if (typeof data === 'object') {
              return JSON.stringify(data);
            }
            return data as string;
          },
        ],

        withCredentials,
      });
      return result.data;
    } catch (error) {
      const apiError = this.createApiError(error as AxiosError<ApiError>);
      throw Object.assign(new Error(apiError.message), apiError);
    }
  }

  static async delete<T>(route: string, headers: Headers = {} as Headers): Promise<T> {
    try {
      const result = await this.axios.delete<T>(route, {
        headers,
        withCredentials: true,
      });
      return result.data;
    } catch (error) {
      const apiError = this.createApiError(error as AxiosError<ApiError>);
      throw Object.assign(new Error(apiError.message), apiError);
    }
  }

  private static mapErrorToMessage(message?: string | Array<ValidationError & { property: string }>): string {
    if (typeof message === 'string') {
      return message;
    }

    if (Array.isArray(message)) {
      return message.map(error => Object.values(error.constraints ?? {})).join(', ');
    }

    return '';
  }
}

export default HttpClient;
